// Pure EOB-entry math shared by EobAmountsForm (display) and RecordPaymentForm
// (submit gating). Split from the component file: react-refresh forbids mixing
// component and function exports. Mirrors the backend posting rules — the
// backend re-validates on submit.
import type { OrpcClient } from '@/orpc/client'
import type { EobResponsibilityKind, PatientInvoiceKind } from '@/model/middleware-types'
import { EOB_RESPONSIBILITY_KINDS } from '@/model/middleware-types'
import { formatCents } from '../utils'

export type EobAmounts = {
  allowedCents: number | null
  patientRespCents: number | null
  /** The insurance payment being recorded; defaults to allowed − patient responsibility. */
  insuranceCents: number | null
  /** Once the biller edits the insurance payment, allowed/PR changes stop overwriting it. */
  insuranceEdited: boolean
}

export function computeAdjustmentCents(
  chargeCents: number | null,
  allowedCents: number | null,
): number | null {
  if (chargeCents === null || allowedCents === null) return null
  return chargeCents - allowedCents
}

/**
 * The insurance payment defaults to allowed − patient responsibility and is editable by
 * the biller (lower, never higher — payer fees). The biller keys in the two EOB numbers;
 * this derived amount is the default being recorded (negative → invalid, the patient
 * responsibility exceeds the allowed amount).
 */
export function computeInsuranceCents(
  allowedCents: number | null,
  patientRespCents: number | null,
): number | null {
  if (allowedCents === null || patientRespCents === null) return null
  return allowedCents - patientRespCents
}

/** Payer fees: what the EOB expects minus what the payer actually sent; null while incomplete. */
export function computeFeesCents(value: EobAmounts): number | null {
  const expected = computeInsuranceCents(value.allowedCents, value.patientRespCents)
  if (expected === null || value.insuranceCents === null) return null
  return expected - value.insuranceCents
}

/** Field error for the insurance payment; null while incomplete or valid. */
export function insuranceError(value: EobAmounts): string | null {
  const expected = computeInsuranceCents(value.allowedCents, value.patientRespCents)
  if (expected === null || value.insuranceCents === null) return null
  if (value.insuranceCents > expected) return 'Cannot exceed allowed − patient responsibility'
  if (value.insuranceCents <= 0) return 'Must be positive'
  return null
}

/**
 * Gate for the submit button: complete and 0 < insurance ≤ allowed − PR. An allowed amount
 * above the remaining charge is NOT blocked here: the server guards it (409 + confirm),
 * and the form warns on the Allowed field.
 */
export function isEobAmountsValid(value: EobAmounts): boolean {
  if (
    value.allowedCents === null ||
    value.patientRespCents === null ||
    value.insuranceCents === null
  ) {
    return false
  }
  return insuranceError(value) === null
}

type CaseDetail = Awaited<ReturnType<OrpcClient['billingV2CaseDetail']>>
/** Structural pick of patientInvoiceSchema — what the form needs to prefill and lock fields. */
export type PatientInvoiceInfo = Pick<
  CaseDetail['patientInvoices'][number],
  'kind' | 'amountCents' | 'status'
>

export const KIND_ORDER: EobResponsibilityKind[] = ['deductible', 'copay', 'coinsurance']

export type PatientBreakdown = Record<EobResponsibilityKind, number | null>

function isEobKind(kind: PatientInvoiceKind): kind is EobResponsibilityKind {
  return (EOB_RESPONSIBILITY_KINDS as readonly string[]).includes(kind)
}

const LOCKED_HINT: Record<string, string> = {
  issued: 'Link sent',
  balanced: 'Collected',
  cancelled: 'Refunded',
}

export function lockedHint(status: PatientInvoiceInfo['status'] | undefined): string | null {
  return LOCKED_HINT[status ?? ''] ?? null
}

/** Draft and locked EOB invoices prefill their field; a kind without an invoice starts empty. Self-pay never has a field. */
export function initialBreakdown(invoices: PatientInvoiceInfo[]): PatientBreakdown {
  const breakdown: PatientBreakdown = { deductible: null, copay: null, coinsurance: null }
  for (const invoice of invoices) {
    if (isEobKind(invoice.kind)) breakdown[invoice.kind] = invoice.amountCents
  }
  return breakdown
}

export function lockedKinds(invoices: PatientInvoiceInfo[]): Set<EobResponsibilityKind> {
  return new Set(
    invoices
      .filter((invoice) => isEobKind(invoice.kind) && invoice.status !== 'draft')
      .map((invoice) => invoice.kind as EobResponsibilityKind),
  )
}

function lockedCents(invoices: PatientInvoiceInfo[]): number {
  return invoices
    .filter((invoice) => isEobKind(invoice.kind) && invoice.status !== 'draft')
    .reduce((sum, invoice) => sum + invoice.amountCents, 0)
}

/** What the editable kinds may add up to: the responsibility minus the locked amounts, never below zero. */
export function breakdownCapCents(
  patientRespCents: number,
  invoices: PatientInvoiceInfo[],
): number {
  return Math.max(0, patientRespCents - lockedCents(invoices))
}

export function breakdownOverpaid(
  invoices: PatientInvoiceInfo[],
  patientRespCents: number | null,
): boolean {
  return patientRespCents !== null && lockedCents(invoices) > patientRespCents
}

export function breakdownError(
  breakdown: PatientBreakdown,
  invoices: PatientInvoiceInfo[],
  patientRespCents: number | null,
): string | null {
  if (patientRespCents === null) return null

  const locked = lockedKinds(invoices)
  const editableCents = KIND_ORDER.filter((kind) => !locked.has(kind)).reduce(
    (sum, kind) => sum + (breakdown[kind] ?? 0),
    0,
  )
  const cap = breakdownCapCents(patientRespCents, invoices)

  if (editableCents > cap) {
    return `Breakdown exceeds the remaining patient responsibility (${formatCents(cap)})`
  }
  return null
}

export function breakdownFields(breakdown: PatientBreakdown, invoices: PatientInvoiceInfo[]) {
  const locked = lockedKinds(invoices)
  const field = (kind: EobResponsibilityKind) =>
    locked.has(kind) ? undefined : (breakdown[kind] ?? 0)
  return {
    deductibleCents: field('deductible'),
    copayCents: field('copay'),
    coinsuranceCents: field('coinsurance'),
  }
}
